/**
 * Event-name constants + concept-tag maps for the immersive modules. Kept
 * centralized so dashboard health, backfill, and analytics can reason about
 * the canonical telemetry surface without grepping module pages.
 */

export const MODULE_SLUGS = [
  "budget_builder",
  "credit_management",
  "career_path",
  "entrepreneurship",
  "development",
  "scam_defense",
] as const;
export type ModuleSlug = (typeof MODULE_SLUGS)[number];

export const BUDGET_EVENTS = {
  STARTED: "module_started",
  INCOME_SELECTED: "budget_income_selected",
  DEDUCTIONS_VIEWED: "budget_deductions_viewed",
  ALLOCATION_STARTED: "budget_allocation_started",
  ALLOCATION_SAVED: "budget_allocation_saved",
  UNDERFUNDED: "budget_category_underfunded",
  OVERFUNDED: "budget_category_overfunded",
  SURPRISE_TRIGGERED: "budget_surprise_event_triggered",
  SURPRISE_RESOLVED: "budget_surprise_event_resolved",
  ADJUSTMENT: "budget_adjustment_made",
  FINAL_OUTCOME: "budget_final_outcome_generated",
  QUIZ_STARTED: "knowledge_check_started",
  QUIZ_PASSED: "knowledge_check_passed",
  QUIZ_FAILED: "knowledge_check_failed",
  COMPLETED: "module_completed",
  RETAKE: "module_retake_required",
} as const;

export const CREDIT_EVENTS = {
  STARTED: "credit_profile_started",
  SPENDING: "credit_spending_decision_made",
  UTILIZATION: "credit_utilization_changed",
  SCORE_SIM: "credit_score_simulated",
  PAYMENT_STRATEGY: "credit_payment_strategy_selected",
  PAYMENT_MADE: "credit_payment_made",
  INTEREST_ACCRUED: "credit_interest_accrued",
  LATE_TRIGGERED: "credit_late_payment_triggered",
  LATE_RESOLVED: "credit_late_payment_resolved",
  PAYOFF_STRATEGY: "debt_payoff_strategy_selected",
  APPLICATION_RESULT: "future_credit_application_result",
  REPORT: "credit_final_report_generated",
  QUIZ_PASSED: "knowledge_check_passed",
  QUIZ_FAILED: "knowledge_check_failed",
  COMPLETED: "module_completed",
} as const;

export const CAREER_EVENTS = {
  PATH_SELECTED: "career_path_selected",
  COMP_VIEWED: "career_compensation_viewed",
  SKILL_INVESTMENT: "career_skill_investment_selected",
  SIDE_HUSTLE: "career_side_hustle_selected",
  SIDE_HUSTLE_STARTUP: "side_hustle_startup_cost_planned",
  TRADEOFF: "career_tradeoff_decision_made",
  FINAL_OUTCOME: "career_final_outcome_generated",
  QUIZ_PASSED: "knowledge_check_passed",
  QUIZ_FAILED: "knowledge_check_failed",
  COMPLETED: "module_completed",
} as const;

export const ENTREPRENEURSHIP_EVENTS = {
  TYPE_SELECTED: "business_type_selected",
  CREATED: "business_created",
  AUDIENCE: "business_audience_selected",
  PRICING: "business_pricing_selected",
  STRATEGY: "business_startup_strategy_selected",
  BUDGET: "business_budget_allocated",
  MONTH_STARTED: "business_month_started",
  DECISION: "business_decision_made",
  EVENT_TRIGGERED: "business_event_triggered",
  EVENT_RESOLVED: "business_event_resolved",
  CASH: "business_cash_changed",
  REVENUE: "business_revenue_recorded",
  EXPENSE: "business_expense_recorded",
  PROFIT: "business_profit_updated",
  CUSTOMERS: "business_customer_count_changed",
  SATISFACTION: "business_satisfaction_changed",
  BRAND: "business_brand_changed",
  BURNOUT: "business_burnout_changed",
  CRUNCH_TRIGGERED: "business_cash_crunch_triggered",
  CRUNCH_RESOLVED: "business_cash_crunch_resolved",
  FINAL_REPORT: "business_final_report_generated",
  QUIZ_PASSED: "knowledge_check_passed",
  QUIZ_FAILED: "knowledge_check_failed",
  COMPLETED: "module_completed",
} as const;

export const DEVELOPMENT_EVENTS = {
  PROJECT_SELECTED: "development_project_selected",
  DOCUMENT_REVIEWED: "development_document_reviewed",
  RED_FLAG: "development_red_flag_identified",
  DUE_DILIGENCE: "development_due_diligence_selected",
  FINANCING: "development_financing_selected",
  BUDGET: "development_budget_allocated",
  CONTRACTOR: "development_contractor_selected",
  PERMIT: "development_permit_strategy_selected",
  EVENT_TRIGGERED: "development_event_triggered",
  EVENT_RESOLVED: "development_event_resolved",
  CRUNCH_TRIGGERED: "development_cash_crunch_triggered",
  CRUNCH_RESOLVED: "development_cash_crunch_resolved",
  LEDGER: "development_ledger_generated",
  EXIT: "development_exit_strategy_selected",
  FINAL_REPORT: "development_final_report_generated",
  QUIZ_PASSED: "knowledge_check_passed",
  QUIZ_FAILED: "knowledge_check_failed",
  COMPLETED: "module_completed",
} as const;

export const SCAM_EVENTS = {
  SCENARIO_STARTED: "scam_scenario_started",
  CHOICE: "scam_choice_made",
  RED_FLAG_OK: "scam_red_flag_identified",
  RED_FLAG_MISSED: "scam_red_flag_missed",
  MONEY_SAVED: "scam_money_saved",
  MONEY_LOST: "scam_money_lost",
  CONFIDENCE: "scam_confidence_changed",
  FINAL_OUTCOME: "scam_final_outcome_generated",
  QUIZ_PASSED: "knowledge_check_passed",
  QUIZ_FAILED: "knowledge_check_failed",
  COMPLETED: "module_completed",
} as const;

/** Default concept tags surfaced by each module — drives mastery rollups. */
export const MODULE_CONCEPT_TAGS: Record<ModuleSlug, string[]> = {
  budget_builder: ["budgeting", "needs_wants_savings", "emergency_fund", "tradeoffs"],
  credit_management: ["credit_utilization", "payment_history", "interest_compounding", "debt_payoff_strategy"],
  career_path: ["career_planning", "compensation_types", "side_hustle_economics", "skill_investment"],
  entrepreneurship: ["unit_economics", "runway", "customer_acquisition", "cash_flow"],
  development: ["leverage", "contingency", "permit_risk", "due_diligence"],
  scam_defense: ["urgency_manipulation", "authority_impersonation", "verification_habit", "too_good_to_be_true", "otp_protection"],
};